import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CNC_WORKFLOW, CNC_LABEL, cncTone, pillClass } from "@/lib/factory";
import { uploadMany, fileUrl } from "@/lib/uploads";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";
import {
  Plus, ShieldCheck, MessageSquare, Camera, X, ChevronRight,
  FileText, AlertCircle,
} from "lucide-react";

/**
 * CNC Designer central ticketing queue + resolution flow.
 * - Raisers (Site Sup, Design, CRM, PMs) see + create tickets.
 * - CNC Designer sees all, replies, approves, uploads revised drawings, transitions workflow.
 * - Photo gate: transitioning to Completed / Dispatched requires ≥1 photo (backend enforces).
 */
export default function CncDesignerPage() {
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const isCnc = isAdmin || roleName === "CNC Designer" || roleType === "director";

  const CNC_TICKET_RAISERS = ["Site Supervisor", "Interior Designer", "Design Manager", "Design Head",
    "CRM Manager", "CRM Executive", "Operations Manager", "Operations Executive",
    "Production Manager", "Procurement Manager"];
  const canRaise = isAdmin || CNC_TICKET_RAISERS.includes(roleName) || roleType === "director";

  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [q, setQ] = useState("");
  const filteredTickets = useMemo(
    () => tickets.filter((t) => projectMatches(t, q)),
    [tickets, q]
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cnc/tickets");
      setTickets(data);
      if (!selectedId && data.length > 0) setSelectedId(data[0].id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [selectedId]);

  const loadSelected = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/cnc/tickets/${id}`);
      setSelected(data);
    } catch { setSelected(null); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);
  useEffect(() => {
    const t = setInterval(() => { load(); if (selectedId) loadSelected(selectedId); }, 20000);
    return () => clearInterval(t);
  }, [load, loadSelected, selectedId]);

  const grouped = useMemo(() => ({
    open: filteredTickets.filter(t => t.status === "open"),
    approved: filteredTickets.filter(t => t.status === "approved"),
    closed: filteredTickets.filter(t => t.status === "closed"),
  }), [filteredTickets]);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 sticky top-0 z-30 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Phase 6 · CNC Desk</p>
          <h1 className="font-display text-3xl text-white mt-1">Ticketing Queue & Resolution</h1>
        </div>
        {canRaise && (
          <button onClick={() => setShowCreate(true)} className="iz-btn-primary flex items-center gap-2" data-testid="new-ticket">
            <Plus className="w-4 h-4"/> Raise ticket
          </button>
        )}
      </div>

      <div className="px-10 py-6 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
        <div className="space-y-3">
          <ProjectSearchBar
            value={q}
            onChange={setQ}
            placeholder="Search tickets by client, project, or city…"
            resultCount={filteredTickets.length}
            totalCount={tickets.length}
            testid="cnc-search"
          />
          <TicketGroup label="Open" tickets={grouped.open} selectedId={selectedId} onSelect={setSelectedId} />
          <TicketGroup label="Approved" tickets={grouped.approved} selectedId={selectedId} onSelect={setSelectedId} />
          <TicketGroup label="Closed" tickets={grouped.closed} selectedId={selectedId} onSelect={setSelectedId} />
          {tickets.length === 0 && (
            <div className="iz-card p-6 text-center text-xs text-gray-500">
              No tickets yet. {canRaise && <button onClick={() => setShowCreate(true)} className="text-[#d4af37] hover:underline">Raise one</button>}
            </div>
          )}
          {tickets.length > 0 && filteredTickets.length === 0 && q.trim() && (
            <ProjectSearchEmpty query={q} testid="cnc-search-empty" />
          )}
        </div>

        <div className="min-w-0">
          {selected ? (
            <TicketDetail ticket={selected} isCnc={isCnc} principalId={principal?.id} onRefresh={() => { load(); loadSelected(selectedId); }}/>
          ) : (
            <div className="iz-card p-12 text-center text-sm text-gray-500">Select a ticket.</div>
          )}
        </div>
      </div>

      {showCreate && <CreateTicketModal onClose={() => setShowCreate(false)} onDone={(id) => { setShowCreate(false); load(); if (id) setSelectedId(id); }} />}
    </div>
  );
}

function TicketGroup({ label, tickets, selectedId, onSelect }) {
  if (tickets.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">{label} · {tickets.length}</p>
      <div className="space-y-1.5">
        {tickets.map(t => (
          <button key={t.id} onClick={() => onSelect(t.id)}
                  className={`iz-card p-3 w-full text-left hover:border-[#d4af37]/40 ${selectedId === t.id ? "border-[#d4af37]" : ""}`}
                  data-testid={`ticket-row-${t.id}`}>
            <div className="flex items-center justify-between">
              <p className="text-white text-sm truncate">{t.subject}</p>
              <WorkflowPill status={t.cnc_workflow} />
            </div>
            <p className="text-[11px] text-gray-500 truncate">{t.project_name} · {t.raised_by_role}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function WorkflowPill({ status }) {
  const cls = pillClass(cncTone(status));
  return <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${cls} shrink-0 ml-2`}>{CNC_LABEL[status] || status}</span>;
}

function TicketDetail({ ticket, isCnc, principalId, onRefresh }) {
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const canReply = isCnc || ticket.raised_by_user_id === principalId;

  const reply = async () => {
    if (!remark.trim()) return;
    setBusy(true);
    try {
      await api.post(`/cnc/tickets/${ticket.id}/reply`, { remark });
      setRemark("");
      toast.success("Reply added");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    try {
      await api.post(`/cnc/tickets/${ticket.id}/approve`);
      toast.success("Ticket stamped as approved");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="iz-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CNC Ticket</p>
            <h2 className="font-display text-2xl text-white mt-1">{ticket.subject}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{ticket.project_name} · raised by {ticket.raised_by_name} ({ticket.raised_by_role})</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {ticket.status === "approved" && <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-500/40 text-emerald-400 bg-emerald-500/5 px-2 py-1 rounded flex items-center gap-1"><ShieldCheck className="w-3 h-3"/>Approved</span>}
            <WorkflowPill status={ticket.cnc_workflow} />
          </div>
        </div>

        <p className="text-sm text-gray-300 whitespace-pre-line mt-4 pt-4 border-t border-[#1a1a1a]">{ticket.description}</p>

        {(ticket.attachment_file_ids || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {ticket.attachment_file_ids.map(fid => (
              <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="iz-btn-secondary text-[10px] font-mono flex items-center gap-1">
                <FileText className="w-3 h-3"/>Attachment
              </a>
            ))}
          </div>
        )}

        {isCnc && ticket.status !== "approved" && (
          <div className="mt-5 pt-4 border-t border-[#1a1a1a] flex flex-wrap gap-2">
            <button onClick={approve} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="approve-stamp">
              <ShieldCheck className="w-3.5 h-3.5"/>Issue Approved Stamp
            </button>
          </div>
        )}
      </div>

      <WorkflowStepper ticket={ticket} canAdvance={isCnc} onRefresh={onRefresh} />
      <RevisedDrawings ticket={ticket} canUpload={isCnc} onRefresh={onRefresh} />

      <div className="iz-card p-5" data-testid="remarks-thread">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Remarks Thread</p>
        {(ticket.remarks_thread || []).length === 0 && <p className="text-xs text-gray-500 italic">No remarks yet.</p>}
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
          {(ticket.remarks_thread || []).map(r => (
            <div key={r.id} className="border-l-2 border-[#d4af37] pl-3 py-1.5" data-testid={`remark-${r.id}`}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{r.by_role} · {r.by_name}</p>
              <p className="text-sm text-gray-200 whitespace-pre-line mt-1">{r.remark}</p>
              <p className="text-[9px] font-mono text-gray-600 mt-1">{new Date(r.at).toLocaleString()}</p>
            </div>
          ))}
        </div>
        {canReply && (
          <div className="mt-4 pt-4 border-t border-[#1a1a1a]">
            <textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Add a remark…"
                      className="iz-input" data-testid="remark-input"/>
            <div className="flex justify-end mt-2">
              <button onClick={reply} disabled={busy || !remark.trim()} className="iz-btn-secondary text-xs flex items-center gap-1.5" data-testid="post-remark">
                <MessageSquare className="w-3.5 h-3.5"/>{busy ? "Posting…" : "Post remark"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WorkflowStepper({ ticket, canAdvance, onRefresh }) {
  const current = ticket.cnc_workflow;
  const [busy, setBusy] = useState(false);

  const transition = async (target) => {
    // Photo gate for completed/dispatched
    if (target === "completed" || target === "dispatched") {
      const input = document.createElement("input");
      input.type = "file"; input.accept = "image/*"; input.multiple = true;
      input.onchange = async () => {
        if (!input.files || input.files.length === 0) {
          toast.error("Verification photo required");
          return;
        }
        setBusy(true);
        try {
          const uploaded = await uploadMany(Array.from(input.files));
          await api.patch(`/cnc/tickets/${ticket.id}/workflow`, { status: target, photo_file_ids: uploaded.map(u => u.id) });
          toast.success(CNC_LABEL[target]);
          onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
      };
      input.click();
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/cnc/tickets/${ticket.id}/workflow`, { status: target, photo_file_ids: [] });
      toast.success(CNC_LABEL[target]);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="iz-card p-5" data-testid="cnc-workflow">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CNC Workflow</p>
        {(current === "completed" || current === "dispatched") && (
          <span className="text-[10px] font-mono text-emerald-400 flex items-center gap-1">
            <Camera className="w-3 h-3"/>Verified with photo
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {CNC_WORKFLOW.map((s, i) => {
          const isCurrent = s.key === current;
          const isPast = CNC_WORKFLOW.findIndex(x => x.key === current) > i;
          const tone = isCurrent ? "gold" : isPast ? "emerald" : "gray";
          return (
            <React.Fragment key={s.key}>
              <div className={`px-2.5 py-1 rounded border text-[9px] font-mono uppercase tracking-widest whitespace-nowrap ${pillClass(tone)}`}
                   data-testid={`cnc-step-${s.key}`}>
                {s.label}
              </div>
              {i < CNC_WORKFLOW.length - 1 && <ChevronRight className="w-3 h-3 text-gray-700 shrink-0"/>}
            </React.Fragment>
          );
        })}
      </div>
      {canAdvance && (
        <div className="mt-4 pt-4 border-t border-[#1a1a1a] flex flex-wrap gap-2">
          {CNC_WORKFLOW.filter(s => s.key !== current).map(s => {
            const needsPhoto = s.key === "completed" || s.key === "dispatched";
            const primary = s.key === "in_progress" || s.key === "completed";
            return (
              <button key={s.key} onClick={() => transition(s.key)} disabled={busy}
                      className={primary ? "iz-btn-primary text-xs flex items-center gap-1.5" : "iz-btn-secondary text-xs flex items-center gap-1.5"}
                      data-testid={`cnc-advance-${s.key}`}>
                {needsPhoto ? <Camera className="w-3 h-3"/> : null}
                Move to · {s.label}
              </button>
            );
          })}
        </div>
      )}
      {(ticket.verification_photos || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#1a1a1a]">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Verification photos</p>
          <div className="grid grid-cols-4 gap-2">
            {ticket.verification_photos.map((vp, i) => (
              vp.file_ids.map(fid => (
                <a key={`${vp.for_status}-${fid}`} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                   className="block aspect-square bg-[#0a0a0a] border border-[#1a1a1a] rounded overflow-hidden">
                  <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover"/>
                </a>
              ))
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RevisedDrawings({ ticket, canUpload, onRefresh }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const uploaded = await uploadMany(Array.from(files));
      await api.post(`/cnc/tickets/${ticket.id}/revised-drawing`, { file_ids: uploaded.map(u => u.id), note: note || null });
      toast.success("Revised drawing uploaded");
      setNote("");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!canUpload && (ticket.revised_drawings || []).length === 0) return null;
  return (
    <div className="iz-card p-5" data-testid="revised-drawings">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Revised CNC Drawings</p>
        {canUpload && (
          <div className="flex items-center gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="iz-input text-xs !py-1"/>
            <button onClick={() => inputRef.current?.click()} disabled={busy} className="iz-btn-secondary text-xs flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5"/>{busy ? "Uploading…" : "Upload"}
            </button>
            <input ref={inputRef} type="file" hidden multiple accept="image/*,.pdf" onChange={(e) => upload(e.target.files)}/>
          </div>
        )}
      </div>
      {(ticket.revised_drawings || []).length === 0 ? (
        <p className="text-xs text-gray-500 italic">No revisions yet.</p>
      ) : (
        <div className="space-y-2">
          {ticket.revised_drawings.map(r => (
            <div key={r.id} className="border border-[#1a1a1a] rounded-md p-3" data-testid={`revised-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-mono text-gray-500">{new Date(r.at).toLocaleString()}</p>
                <span className="text-[10px] font-mono text-[#d4af37]">{r.file_ids?.length || 0} file(s)</span>
              </div>
              {r.note && <p className="text-xs text-gray-300 mt-1">{r.note}</p>}
              <div className="flex flex-wrap gap-2 mt-2">
                {(r.file_ids || []).map(fid => (
                  <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="iz-btn-secondary text-[10px] font-mono">Open</a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateTicketModal({ onClose, onDone }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = React.useRef(null);

  useEffect(() => {
    api.get("/procurement/orders").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!projectId || !subject.trim() || !description.trim()) { setErr("Project, subject, and description are required"); return; }
    setBusy(true);
    try {
      let attachment_file_ids = [];
      if (files.length > 0) {
        const uploaded = await uploadMany(files);
        attachment_file_ids = uploaded.map(u => u.id);
      }
      const { data } = await api.post("/cnc/tickets", {
        procurement_order_id: projectId, subject, description, attachment_file_ids,
      });
      toast.success("Ticket raised");
      onDone(data.id);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="create-ticket-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="iz-card w-full max-w-lg p-8 relative max-h-[92vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">CNC · new ticket</p>
        <h2 className="font-display text-2xl text-white mb-6">Raise Clarification</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="iz-input" required data-testid="ticket-project">
              <option value="">Select project…</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_name} · {p.client_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="iz-input" required data-testid="ticket-subject"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Description</label>
            <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className="iz-input" required data-testid="ticket-description"/>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-gray-400">Attachments</label>
              <button type="button" onClick={() => inputRef.current?.click()} className="iz-btn-secondary text-xs flex items-center gap-1"><Plus className="w-3 h-3"/>Add</button>
              <input ref={inputRef} type="file" hidden multiple onChange={(e) => setFiles([...files, ...Array.from(e.target.files)])}/>
            </div>
            <p className="text-[10px] font-mono text-gray-600">{files.length} file(s) attached.</p>
          </div>
          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2 flex items-center gap-2"><AlertCircle className="w-3.5 h-3.5"/>{err}</div>}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="iz-btn-primary" data-testid="ticket-submit">{busy ? "Raising…" : "Raise ticket"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
